import {
  AccountInfo,
  InteractionStatus,
  SilentRequest
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useTranslation } from "react-i18next";

import { ErrorPage, LoadingPage } from "@digital-services/shell";

import {
  addUserRequestHeadersInterceptor,
  ejectRequestInterceptor
} from "@utils/axiosInstance";

import { tokenRequest } from "../../authConfig";
import { buildUserFromAccessToken } from "./helpers";
import { User } from "./models/User";
import { UserContext } from "./UserContext";

const UserContextProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation(undefined, { keyPrefix: "account" });
  const { instance, inProgress } = useMsal();

  const account = instance.getActiveAccount();

  const [user, setUser] = useState<User>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const getAccessToken = useCallback(
    async (forceRefresh?: boolean): Promise<string> => {
      const account = instance.getActiveAccount();
      if (!account) {
        throw new Error("No active account");
      }

      const request: SilentRequest = {
        ...tokenRequest,
        forceRefresh: forceRefresh ?? false,
        account: account
      };

      let accessToken = "";
      const result = await instance.acquireTokenSilent(request);
      accessToken = result.accessToken;

      if (!accessToken || accessToken === "") await instance.logoutRedirect();

      return accessToken;
    },
    [instance]
  );

  useEffect(() => {
    let id: number;
    const applyUserAndInterceptor = async (account: AccountInfo | null) => {
      if (account === null) {
        setUser(undefined);
        return;
      }

      const token = await getAccessToken();
      const user = buildUserFromAccessToken(token);

      if (!user) {
        setUser(undefined);
        return;
      } else {
        setUser(user);
      }

      id = addUserRequestHeadersInterceptor(() => getAccessToken());
    };

    if (inProgress !== InteractionStatus.None) {
      return;
    }
    setIsLoading(true);
    applyUserAndInterceptor(account)
      .catch(() => setError(t("signin.user.load.error.message")))
      .finally(() => setIsLoading(false));

    return () => {
      ejectRequestInterceptor(id);
    };
    // eslint-disable-next-line
  }, [inProgress]);

  const memoizedChildren = useMemo(
    () => <>{children}</>,
    // eslint-disable-next-line
    []
  );

  const contextValue = useMemo(
    () => ({
      user: user!,
      getAccessToken
    }),
    [getAccessToken, user]
  );

  if (inProgress !== InteractionStatus.None) {
    return;
  }

  if (isLoading || !user) {
    // is loading centered, with spinner
    // TBD to be removed when migrated to fully new SHELL+ AUTH MSAL approach
    return <LoadingPage message={t("authenticate.message")} />;
  }

  if (error) {
    return <ErrorPage title="User Context Error" message={error} />;
  }

  return (
    <UserContext.Provider value={contextValue}>
      {memoizedChildren}
    </UserContext.Provider>
  );
};

export default UserContextProvider;
